import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareWarning, ChevronRight, CheckCircle2, UserX, MinusCircle, Undo2, Loader2, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATE = {
  written: { label: "Attended — feedback written", color: "#CCFF00", Icon: CheckCircle2 },
  waived: { label: "Attended — feedback waived", color: "#A3A3A3", Icon: MinusCircle },
  no_show: { label: "No-show", color: "#FF4500", Icon: UserX },
};

export const ResolutionBadge = ({ p }) => {
  const m = STATE[p.resolution];
  if (!m) return <span className="text-[10px] uppercase tracking-wider font-bold text-[#FF9F1C]" data-testid={`roster-resolution-${p.id}`}>Unresolved</span>;
  return <span className="text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1" style={{ color: m.color }} data-testid={`roster-resolution-${p.id}`}><m.Icon className="w-3 h-3" /> {m.label}</span>;
};

const UnresolvedRow = ({ p, sessionId, busy, act }) => {
  const [waiving, setWaiving] = useState(false);
  const [confirmNoShow, setConfirmNoShow] = useState(false);
  const [note, setNote] = useState("");
  return (
    <div className="py-2.5 border-b border-[#1F1F1F] last:border-0" data-testid={`resolve-row-${p.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold">{p.name}</span>
        <div className="flex flex-wrap gap-1.5">
          {p.booking_id && <Link to={`/coach/feedback?booking=${p.booking_id}&student=${p.id}&from=${sessionId}`} className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 bg-[#CCFF00] text-black font-bold inline-flex items-center gap-1" data-testid={`resolve-write-${p.id}`}>
            Write feedback <ChevronRight className="w-3 h-3" />
          </Link>}
          <button type="button" disabled={busy} onClick={() => { setWaiving((v) => !v); setConfirmNoShow(false); }} className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-[#404040] hover:border-white disabled:opacity-50" data-testid={`resolve-waive-${p.id}`}>Waive feedback</button>
          <button type="button" disabled={busy} onClick={() => { setConfirmNoShow((v) => !v); setWaiving(false); }} className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-[#FF4500]/60 text-[#FF4500] hover:bg-[#FF4500]/10 disabled:opacity-50" data-testid={`resolve-noshow-${p.id}`}>No-show</button>
        </div>
      </div>
      {confirmNoShow && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-[#A3A3A3]">
          <span>{p.name} will be notified that a no-show was logged. Credits and payments are not changed.</span>
          <button type="button" disabled={busy} onClick={() => act(p, "no_show")} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#FF4500] text-[#FF4500] font-bold disabled:opacity-50" data-testid={`resolve-noshow-confirm-${p.id}`}>Confirm no-show</button>
        </div>
      )}
      {waiving && (
        <div className="mt-2 flex flex-wrap gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 400))} placeholder="Optional note"
            className="flex-1 min-w-[180px] bg-[#0A0A0A] border border-[#262626] px-3 py-1.5 text-xs focus:outline-none focus:border-[#CCFF00]" data-testid={`resolve-waive-note-${p.id}`} />
          <button type="button" disabled={busy} onClick={() => act(p, "waived", note)} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-white font-bold disabled:opacity-50" data-testid={`resolve-waive-confirm-${p.id}`}>Confirm waive</button>
        </div>
      )}
    </div>
  );
};

const WaiveAll = ({ sessionId, count, onDone }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offerings/${sessionId}/waive-all-feedback`, { note });
      toast.success(`Feedback waived for ${data.waived} player${data.waived === 1 ? "" : "s"}`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't waive feedback");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#404040] hover:border-white inline-flex items-center gap-1" data-testid="waive-all-feedback-btn">
        <CheckCheck className="w-3.5 h-3.5" /> Waive feedback for all
      </button>
      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent data-testid="waive-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Waive feedback for {count} player{count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>Every unresolved player will be marked "Attended — feedback waived". Players already resolved are not changed.</AlertDialogDescription>
          </AlertDialogHeader>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 400))} rows={2}
            placeholder="Optional note, e.g. Club session — no individual feedback required"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" data-testid="waive-all-note" />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="waive-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); apply(); }} data-testid="waive-all-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Waive for all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Past-session player resolution: written / waived / no-show. Read-only summary once all are resolved.
export function PlayerResolutionPanel({ session, onChanged, highlight }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const roster = (session.roster || []).filter((p) => p.booking_id);
  const unresolved = roster.filter((p) => !p.resolution);
  useEffect(() => { if (highlight && unresolved.length) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [highlight, unresolved.length]);
  if (!roster.length) return null;

  const act = async (p, action, note = "") => {
    setBusy(true);
    try {
      await api.post(`/offerings/${session.id}/players/${p.id}/resolve`, { action, note });
      const sname = session.title || "this session";
      const msg = action === "undo"
        ? (p.resolution === "no_show" ? `No-show removed for ${p.name} — ${sname}` : `Feedback waiver removed for ${p.name} — ${sname}`)
        : action === "no_show" ? `No-show recorded for ${p.name} — ${sname}` : `Feedback waived for ${p.name} — ${sname}`;
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't update");
    } finally { setBusy(false); }
  };

  if (session.session_completed) {
    const canUndo = !session.session_locked;
    return (
      <div className="card-surface p-5 border-l-4 border-[#CCFF00] space-y-2" data-testid="session-resolution-summary">
        <div className="flex items-center gap-2 text-[#CCFF00] font-bold text-sm"><CheckCircle2 className="w-4 h-4" /> Session completed — all players resolved</div>
        {canUndo && session.undo_until && (
          <div className="text-xs text-[#A3A3A3]" data-testid="undo-window-note">
            Made a mistake? Waived and no-show players can be undone until {new Date(session.undo_until).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.
          </div>
        )}
        {roster.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm" data-testid={`summary-row-${p.id}`}>
            <span className="font-semibold">{p.name}</span>
            <span className="text-right inline-flex items-center gap-2">
              <span><ResolutionBadge p={p} />{p.resolution_note ? <span className="block text-xs text-[#737373] italic">{p.resolution_note}</span> : null}</span>
              {canUndo && p.resolution !== "written" && (
                <button type="button" disabled={busy} onClick={() => act(p, "undo")} className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid={`resolve-undo-${p.id}`}>
                  <Undo2 className="w-3 h-3" /> Undo
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const resolved = roster.filter((p) => p.resolution);
  const nextUp = unresolved[0];
  return (
    <div ref={ref} className={`card-surface p-5 border-l-4 border-[#FF4500] space-y-3 scroll-mt-24 ${highlight ? "ring-1 ring-[#FF4500]/60" : ""}`} data-testid="session-awaiting-feedback">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[#FF4500] font-bold text-sm">
          <MessageSquareWarning className="w-4 h-4" /> Awaiting feedback: {unresolved.length} player{unresolved.length === 1 ? "" : "s"}
        </div>
        <div className="flex flex-wrap gap-2">
          {nextUp?.booking_id && (
            <Link to={`/coach/feedback?booking=${nextUp.booking_id}&student=${nextUp.id}&from=${session.id}`} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1" data-testid="resolve-next-player">
              Next player: {nextUp.name} <ChevronRight className="w-3 h-3" />
            </Link>
          )}
          <WaiveAll sessionId={session.id} count={unresolved.length} onDone={onChanged} />
        </div>
      </div>
      <div>{unresolved.map((p) => <UnresolvedRow key={p.id} p={p} sessionId={session.id} busy={busy} act={act} />)}</div>
      {resolved.length > 0 && (
        <div className="pt-2 border-t border-[#262626] space-y-1.5">
          <div className="label-mono text-[10px] text-[#737373]">RESOLVED</div>
          {resolved.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`resolved-row-${p.id}`}>
              <span>{p.name}</span>
              <span className="inline-flex items-center gap-2">
                <ResolutionBadge p={p} />
                {p.resolution !== "written" && (
                  <button type="button" disabled={busy} onClick={() => act(p, "undo")} className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid={`resolve-undo-${p.id}`}>
                    <Undo2 className="w-3 h-3" /> Undo
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
